import type { CampaignAnalyticsGranularity, CampaignAnalyticsResponse } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import {
  PerformancePanel,
  PerformancePanelEmptyState,
} from "@/components/shared/performance/PerformancePanel";
import {
  DEFAULT_PERFORMANCE_SLOTS,
  PERFORMANCE_METRICS,
  type PerformanceMetric,
} from "@/components/shared/performance/performance";
import { useAttributionMix } from "@/hooks/use-attribution-note";
import { useShopScope } from "@/hooks/use-shop-scope";

interface CampaignPerformancePanelProps {
  data: CampaignAnalyticsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  metric: PerformanceMetric;
  granularity: CampaignAnalyticsGranularity;
  onMetricChange: (metric: PerformanceMetric) => void;
  onGranularityChange: (granularity: CampaignAnalyticsGranularity) => void;
  onRetry: () => void;
}

/**
 * Campaign-list wrapper around the shared PerformancePanel: contributes the
 * connection / campaign-match empty states the campaign analytics response
 * carries, and the campaign page's own slot/collapse persistence.
 */
export function CampaignPerformancePanel({
  data,
  isLoading,
  isFetching,
  isError,
  metric,
  granularity,
  onMetricChange,
  onGranularityChange,
  onRetry,
}: CampaignPerformancePanelProps): React.ReactElement {
  const { t } = useTranslation("campaign");
  // The campaign list reads the analytics scope, not the operational current shop.
  const attributionMix = useAttributionMix(useShopScope().scopeShopIds);
  const emptyState = !data ? undefined : !data.connected ? (
    <PerformancePanelEmptyState
      title={t("performance.disconnectedTitle")}
      description={t("performance.disconnectedDescription")}
    />
  ) : data.matchedCampaigns === 0 ? (
    <PerformancePanelEmptyState
      title={t("performance.noMatchTitle")}
      description={t("performance.noMatchDescription")}
    />
  ) : undefined;

  return (
    <PerformancePanel
      metrics={PERFORMANCE_METRICS}
      defaultSlots={DEFAULT_PERFORMANCE_SLOTS}
      kpis={data?.kpis ?? []}
      trend={data?.trend ?? []}
      dataThrough={data?.dataThrough ?? null}
      attributionMix={attributionMix}
      metric={metric}
      granularity={granularity}
      onMetricChange={onMetricChange}
      onGranularityChange={onGranularityChange}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      onRetry={onRetry}
      collapseStorageKey="adpilot:campaign-performance:v2"
      slotsStorageKey="adpilot:campaign-performance-slots:v1"
      emptyState={emptyState}
    />
  );
}
